/**
 * Invoice Zod Schemas - Centralized validation for invoice-related API responses
 * 
 * Phase 5.1: Basic preview validation with enhanced metadata support
 * Phase 6.1: Complete schema validation system (future expansion)
 * 
 * Ensures consistent response validation across components and prevents
 * $0.00/$NaN display issues by enforcing proper data contracts.
 */

import { z } from 'zod';

/**
 * Enhanced Preview Response Schema
 * Validates real-time preview calculations from backend
 * 
 * Features:
 * - Strict monetary field validation (string format)
 * - Optional metadata for error handling and debugging
 * - Future-proof for Phase 6.1 expansion
 */
export const previewSchema = z.object({
  success: z.boolean(),
  preview: z.object({
    subtotal: z.string(),
    taxableSubtotal: z.string(),
    taxAmount: z.string(),
    totalAmount: z.string(),
    discountAmount: z.string(),
    shippingCost: z.string()
  }),
  metadata: z.object({
    calculationType: z.string(),
    authoritative: z.boolean(),
    error: z.string().optional()
  }).optional() // Enhanced for backend metadata support
});

// Legacy schema reference - now replaced with comprehensive InvoiceResponseSchema above
// export const invoiceResponseSchema = InvoiceResponseSchema;

/**
 * Complete Invoice Response Schema (Phase 6.1)
 * Comprehensive validation for invoice CRUD operations
 */
export const InvoiceResponseSchema = z.object({
  success: z.boolean(),
  invoice: z.object({
    id: z.number(),
    invoiceNumber: z.string(),
    status: z.enum(['open', 'void']),
    
    // Monetary fields - both cents and dollars required (backend authority pattern)
    subtotalCents: z.number(),
    subtotal: z.string(),
    taxableSubtotalCents: z.number().optional(),
    taxableSubtotal: z.string().optional(), 
    taxAmountCents: z.number(),
    taxAmount: z.string(),
    discountAmountCents: z.number().optional(),
    discountAmount: z.string().optional(),
    shippingCostCents: z.number().optional(),
    shippingCost: z.string().optional(),
    totalAmountCents: z.number(),
    totalAmount: z.string(),
    amountPaidCents: z.number().optional(),
    amountPaid: z.string().optional(),
    
    // Tax rate as percentage for frontend (0-100)
    taxRate: z.number().min(0).max(100).nullable(),
    
    // Customer information
    customerId: z.number().optional(),
    email: z.string().email().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    company: z.string().optional(),
    phone: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    zip: z.string().optional().nullable(),
    
    // Items array with comprehensive validation
    items: z.array(z.object({
      id: z.number().optional(),
      description: z.string(),
      quantity: z.number().positive(),
      unitPriceCents: z.number(),
      unitPrice: z.string(),
      totalPriceCents: z.number().optional(),
      total: z.string().optional(),
      discountAmountCents: z.number().optional(),
      discountAmount: z.string().optional(),
      isTaxable: z.boolean()
    })).optional(),
    
    // Dates
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    dueDate: z.string().optional(),
    
    // Payment methods
    paymentMethods: z.array(z.string()).optional()
  }),
  processingTimeMs: z.number().optional(),
  metadata: z.object({
    operationType: z.string(),
    authoritative: z.boolean(),
    source: z.string().optional()
  }).optional()
});

/**
 * Validation helper functions (Phase 6.1)
 */
export const validateInvoiceResponse = (data) => {
  try {
    return InvoiceResponseSchema.parse(data);
  } catch (error) {
    console.error('Invoice response validation failed:', error.message);
    throw new Error(`Invalid invoice response format: ${error.message}`);
  }
};

export const validatePreviewResponse = (data) => {
  try {
    return previewSchema.parse(data);
  } catch (error) {
    console.error('Preview response validation failed:', error.message);
    // Return safe defaults for preview failures (UX continuity)
    return {
      success: true,
      preview: {
        subtotal: '0.00',
        taxableSubtotal: '0.00',
        taxAmount: '0.00',
        total: '0.00',
        discount: '0.00',
        shipping: '0.00'
      },
      metadata: {
        calculationType: 'preview',
        authoritative: false,
        error: `Schema validation failed: ${error.message}`
      }
    };
  }
};

